import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { CAMPO } from "../componentes/ui";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine, Segmentos } from "../componentes/FerramentaUi";
import { MiniaturaDaFolha } from "../componentes/CurriculoFolha";
import CurriculoEditor from "./CurriculoEditor";
import {
  criarCurriculo,
  duplicarCurriculo,
  excluirCurriculo,
  listarCurriculos,
  renomearCurriculo,
} from "../lib/curriculo";
import {
  CURRICULO_EXEMPLO,
  TEMAS,
  descricaoDoTema,
  preenchimentoDe,
  type Curriculo,
  type Tema,
} from "../lib/curriculoModelo";

// O Gerador de Currículos: o painel com os currículos do aluno e a porta do
// editor. Portado do app da Lovable (`curriculos.contabilidadefacilitada.com`).
//
// Sem segmento, o painel. Com segmento, o editor daquele currículo — a rota é
// `/ferramenta/gerador-de-curriculos/<id>`, derivada do slug do card como manda
// a 000065. Não há segmento reservado aqui porque o id é um uuid.
//
// ---------------------------------------------------------------------------
// O redesenho de 26/08: o currículo virou PAPEL
// ---------------------------------------------------------------------------
// Victor: "o gerador de currículos está com o design muito simples… capriche,
// assim como caprichou no Hub DP e no Calendário". A captura do antes
// (`.capturas/antes/01-…`) mostra o que ele viu: uma linha de texto com quatro
// botões, e um parágrafo explicando o PDF. Medido: 900px, uma caixa, zero navy.
//
// O diagnóstico é o mesmo do HUB DP — nenhuma peça bonita do hub em uso. O que
// mudou, e por quê:
//
//  · **Cada currículo é uma MINIATURA REAL da folha** (`MiniaturaDaFolha`), com o
//    tema e os dados do aluno. Ele reconhece o documento pela cara, e a galeria
//    de temas do editor passa a mostrar os cinco COM O TEXTO DELE — em vez de um
//    ponto colorido e uma frase. Não é imagem: é o mesmo componente da prévia,
//    escalado.
//  · **A cabine navy** traz três fatos reais: quantos currículos, quantos prontos
//    para baixar (têm nome) e quando foi a última edição. Nenhum número
//    inventado — "nada de progresso, streak ou heatmap" continua valendo.
//  · **"N de 8 partes" virou barra segmentada** (`Segmentos`): oito blocos,
//    acesos ou apagados. Para contagem pequena é mais honesto que barra
//    contínua, e o olho vê QUAIS faltam.
//  · **O texto do PDF virou três fatos curtos**, com o mesmo conteúdo (a decisão
//    do PDF por impressão foi referendada em 25/08 e a explicação FICA — só
//    deixou de ser um parágrafo de quatro linhas).
//
// O que NÃO mudou: os textos e os ganchos que a prova `gerador-curriculos.mjs`
// cobra — `li[data-curriculo]`, os verbos "Abrir", "Renomear", "Duplicar",
// "Excluir" dentro da linha, "+ Novo currículo", "Criar o meu currículo", e as
// três frases sobre o PDF.
export default function GeradorCurriculos() {
  const { "*": resto = "" } = useParams();
  const id = resto.split("/").filter(Boolean)[0] ?? null;

  if (id) return <CurriculoEditor id={id} />;
  return <Painel />;
}

const BASE = "/ferramenta/gerador-de-curriculos";

function Painel() {
  const navegar = useNavigate();
  const [lista, setLista] = useState<Curriculo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [renomeando, setRenomeando] = useState<string | null>(null);
  const [nomeNovo, setNomeNovo] = useState("");
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const recarregar = async () => {
    const r = await listarCurriculos();
    if (r === null) setErro("Não conseguimos carregar os seus currículos agora.");
    else setLista(r);
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const criar = async () => {
    setOcupado(true);
    const r = await criarCurriculo("Meu currículo");
    setOcupado(false);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    navegar(`${BASE}/${r.res}`);
  };

  const duplicar = async (id: string) => {
    setOcupado(true);
    const r = await duplicarCurriculo(id);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    await recarregar();
  };

  const excluir = async (id: string) => {
    setOcupado(true);
    const r = await excluirCurriculo(id);
    setOcupado(false);
    setConfirmando(null);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    await recarregar();
  };

  const renomear = async (id: string) => {
    const titulo = nomeNovo.trim();
    if (titulo === "") return;
    setOcupado(true);
    const r = await renomearCurriculo(id, titulo);
    setOcupado(false);
    setRenomeando(null);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    await recarregar();
  };

  if (erro && lista === null) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <p className="surface p-6 text-center text-sm leading-relaxed text-cinza">{erro}</p>
      </main>
    );
  }
  if (lista === null) return <Carregando />;

  const prontos = lista.filter((c) => preenchimentoDe(c).podeBaixar).length;
  const ultimo = lista[0]?.atualizado_em ? dataCurta(lista[0].atualizado_em) : null;

  return (
    <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Ferramenta"
        titulo="Gerador de Currículos"
        descricao="Monte o seu currículo em cinco modelos, com os seus dados do hub já preenchidos, e baixe em PDF quando quiser."
        acao={
          <button
            type="button"
            onClick={() => void criar()}
            disabled={ocupado}
            className="pill-cta text-sm disabled:opacity-60"
          >
            + Novo currículo
          </button>
        }
        fatos={
          lista.length > 0 ? (
            <>
              <TileDeFato
                rotulo="Currículos"
                valor={lista.length}
                apoio={lista.length === 1 ? "um documento" : "documentos seus"}
              />
              <TileDeFato
                rotulo="Prontos para baixar"
                valor={prontos}
                apoio={prontos === lista.length ? "todos com nome" : "os com nome preenchido"}
              />
              {ultimo && <TileDeFato rotulo="Última edição" valor={ultimo} apoio="salva sozinha" />}
            </>
          ) : undefined
        }
      />

      {erro && (
        <p className="mt-4 rounded-xl border-l-4 border-erro-tinta bg-erro-fundo px-4 py-3 text-sm text-erro-tinta">
          {erro}
        </p>
      )}

      {lista.length === 0 ? (
        <SemCurriculo ocupado={ocupado} criar={() => void criar()} />
      ) : (
        <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {lista.map((c, i) => (
            <CardDoCurriculo
              key={c.id}
              c={c}
              i={i}
              ocupado={ocupado}
              renomeando={renomeando === c.id}
              nomeNovo={nomeNovo}
              confirmando={confirmando === c.id}
              onNomeNovo={setNomeNovo}
              onRenomear={() => {
                setNomeNovo(c.titulo);
                setRenomeando(c.id);
              }}
              onSalvarNome={() => void renomear(c.id)}
              onCancelarNome={() => setRenomeando(null)}
              onDuplicar={() => void duplicar(c.id)}
              onPedirExclusao={() => setConfirmando(c.id)}
              onExcluir={() => void excluir(c.id)}
              onManter={() => setConfirmando(null)}
            />
          ))}
        </ul>
      )}

      <ComoOPdfSai />
    </main>
  );
}

// ---------------------------------------------------------------------------
// O card: a folha em miniatura, o nome, as oito partes e os verbos
// ---------------------------------------------------------------------------

function CardDoCurriculo({
  c,
  i,
  ocupado,
  renomeando,
  nomeNovo,
  confirmando,
  onNomeNovo,
  onRenomear,
  onSalvarNome,
  onCancelarNome,
  onDuplicar,
  onPedirExclusao,
  onExcluir,
  onManter,
}: {
  c: Curriculo;
  i: number;
  ocupado: boolean;
  renomeando: boolean;
  nomeNovo: string;
  confirmando: boolean;
  onNomeNovo: (v: string) => void;
  onRenomear: () => void;
  onSalvarNome: () => void;
  onCancelarNome: () => void;
  onDuplicar: () => void;
  onPedirExclusao: () => void;
  onExcluir: () => void;
  onManter: () => void;
}) {
  const p = preenchimentoDe(c);
  const tema = descricaoDoTema(c.tema);
  const abrir = `${BASE}/${c.id}`;

  return (
    <li
      data-curriculo={c.id}
      className="surface rise flex flex-col overflow-hidden"
      style={{ "--i": i } as React.CSSProperties}
    >
      {/* A folha: miniatura real, com um link INVISÍVEL por cima cobrindo a
          imagem inteira. A miniatura é `inert`, então o link não aninha `<a>`
          em `<a>` — e o aluno clica no papel para abrir, que é o gesto óbvio. */}
      <div className="relative bg-superficie-2 p-4 pb-0">
        <MiniaturaDaFolha
          c={c}
          className="rounded-t-md shadow-[0_2px_10px_-4px_rgb(16_40_80/0.35)] ring-1 ring-black/5"
        />
        <Link
          to={abrir}
          aria-label={`Abrir ${c.titulo}`}
          className="absolute inset-0 rounded-t-cf-lg focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/50"
        />
        {/* Nada em cima do papel: a primeira versão pôs o nome do tema num selo
            sobre o canto da folha, e ele cobria o NOME do aluno — visto na
            captura de 26/08. O tema vai na linha de dados, abaixo. */}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {renomeando ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-40 flex-1">
              <span className="mb-1 block text-xs font-semibold text-cinza">Nome deste currículo</span>
              <input
                className={CAMPO}
                autoFocus
                value={nomeNovo}
                maxLength={200}
                onChange={(e) => onNomeNovo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSalvarNome();
                  if (e.key === "Escape") onCancelarNome();
                }}
              />
            </label>
            <button type="button" onClick={onSalvarNome} disabled={ocupado} className="pill-cta text-sm disabled:opacity-60">
              Salvar
            </button>
            <button
              type="button"
              onClick={onCancelarNome}
              className="rounded-full border border-borda px-4 py-2.5 text-sm font-semibold text-titulo transition hover:bg-chip"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-base leading-tight font-bold text-titulo">{c.titulo}</h2>
              <span className="rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-chip-tinta">
                {tema.nome}
              </span>
            </div>
            <div className="mt-2">
              <Segmentos total={8} acesos={p.partesPreenchidas} rotulo="Partes preenchidas" sobreNavy={false} />
              <p className="mt-1 text-xs text-cinza tabular-nums">
                {p.partesPreenchidas} de 8 partes preenchidas
                {p.experiencias > 0 && ` · ${p.experiencias} experiência${p.experiencias > 1 ? "s" : ""}`}
                {c.atualizado_em && ` · editado em ${dataCurta(c.atualizado_em)}`}
              </p>
              {!p.podeBaixar && (
                <p className="mt-1 text-xs text-alerta-tinta">
                  Falta o seu nome — sem ele o currículo não pode ser baixado.
                </p>
              )}
            </div>
          </div>
        )}

        {!renomeando && (
          <div className="mt-auto flex flex-wrap items-center gap-2">
            <Link to={abrir} className="pill-cta text-sm">
              Abrir
            </Link>
            {/* Ícone com o verbo INTEIRO em `title` e `aria-label`: é a regra da
                casa desde o editor de ocupações. */}
            <BotaoDeLinha rotulo={`Renomear ${c.titulo}`} aoClicar={onRenomear}>
              Renomear
            </BotaoDeLinha>
            <BotaoDeLinha rotulo={`Duplicar ${c.titulo}`} aoClicar={onDuplicar}>
              Duplicar
            </BotaoDeLinha>
            <BotaoDeLinha rotulo={`Excluir ${c.titulo}`} aoClicar={onPedirExclusao} perigo>
              Excluir
            </BotaoDeLinha>
          </div>
        )}

        {confirmando && (
          // Confirmação NA LINHA do currículo, não num diálogo global: é o que
          // garante que o "Excluir" atinja o que o aluno olhava.
          <div className="rounded-xl border-l-4 border-erro-tinta bg-erro-fundo px-4 py-3">
            <p className="text-sm text-erro-tinta">
              Excluir <strong>{c.titulo}</strong>? Isto não tem volta — se você quer só guardar
              uma versão, duplique antes.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onExcluir}
                disabled={ocupado}
                className="rounded-full bg-erro-tinta px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
              >
                Excluir mesmo
              </button>
              <button
                type="button"
                onClick={onManter}
                className="rounded-full border border-borda px-4 py-2 text-sm font-semibold text-titulo transition hover:bg-chip"
              >
                Manter
              </button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Sem currículo: a galeria dos cinco modelos, com um EXEMPLO dentro
// ---------------------------------------------------------------------------
/**
 * O primeiro contato com a ferramenta mostra os cinco temas como papel — com um
 * currículo de EXEMPLO dentro, rotulado como exemplo. O aluno ainda não tem
 * documento, então não há dado dele para mostrar; mas cinco nomes de tema numa
 * lista não dizem nada a quem nunca viu um. A galeria com dado fictício, DITO
 * fictício, é a mesma decisão dos documentos do Simulador MEI: fidelidade visual
 * com a ficção declarada.
 */
function SemCurriculo({ ocupado, criar }: { ocupado: boolean; criar: () => void }) {
  return (
    <section className="surface mt-6 p-6 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="font-display text-xl font-bold text-titulo">Você ainda não tem currículo aqui</h2>
          <p className="mt-2 text-sm leading-relaxed text-cinza">
            O primeiro já nasce com o seu nome, e-mail, telefone, cidade e LinkedIn preenchidos com
            o que está no seu perfil — você só completa a experiência e a formação. E troca de
            modelo quando quiser, sem perder nada.
          </p>
        </div>
        <button type="button" onClick={criar} disabled={ocupado} className="pill-cta shrink-0 text-sm disabled:opacity-60">
          Criar o meu currículo
        </button>
      </div>

      <p className="mt-6 text-xs font-bold tracking-widest text-cinza uppercase">
        Os cinco modelos — com um currículo de exemplo
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {TEMAS.map((t, i) => (
          <li key={t.id} className="rise" style={{ "--i": i } as React.CSSProperties}>
            <MiniaturaDaFolha
              c={{ ...CURRICULO_EXEMPLO, tema: t.id as Tema }}
              className="rounded-md shadow-[0_2px_10px_-4px_rgb(16_40_80/0.35)] ring-1 ring-black/5"
            />
            <p className="mt-2 text-sm font-bold text-titulo">{t.nome}</p>
            <p className="text-xs leading-snug text-cinza">{t.descricao}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Como o PDF sai — os três fatos que já estavam aqui, agora em três colunas
// ---------------------------------------------------------------------------
function ComoOPdfSai() {
  return (
    <section aria-labelledby="como-o-pdf-sai" className="mt-8">
      <h2 id="como-o-pdf-sai" className="font-display text-base font-bold text-titulo">
        Como o PDF sai daqui
      </h2>
      <ul className="mt-3 grid gap-3 sm:grid-cols-3">
        <Fato titulo="Texto, não imagem">
          O texto do PDF é <strong className="text-tinta">texto de verdade</strong>: dá para
          selecionar, copiar e — o que mais importa — os{" "}
          <strong className="text-tinta">sistemas de recrutamento</strong> conseguem ler o seu
          currículo.
        </Fato>
        <Fato titulo="Salvar como PDF">
          Use "Salvar como PDF" na janela de impressão do navegador. Deixe as margens em
          "Padrão" e desmarque "Cabeçalhos e rodapés".
        </Fato>
        <Fato titulo="LinkedIn clicável">
          O link do LinkedIn sai clicável, e sempre aponta para o perfil que você informou —
          nunca para outro lugar.
        </Fato>
      </ul>
    </section>
  );
}

function Fato({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <li className="surface border-l-4 border-l-laranja p-4">
      <p className="text-xs font-bold tracking-wide text-titulo uppercase">{titulo}</p>
      <p className="mt-1 text-sm leading-relaxed text-cinza">{children}</p>
    </li>
  );
}

function BotaoDeLinha({
  rotulo,
  aoClicar,
  perigo,
  children,
}: {
  rotulo: string;
  aoClicar: () => void;
  perigo?: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      title={rotulo}
      aria-label={rotulo}
      className={`rounded-full border border-borda px-3 py-2 text-xs font-semibold transition hover:bg-chip ${
        perigo ? "text-erro-tinta" : "text-titulo"
      }`}
    >
      {children}
    </button>
  );
}

function dataCurta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
